import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

// --- Styled Components ---
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f4f7f6;
  padding: 20px;
`;

const FormBox = styled.form`
  background: #fff;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 30px;
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: #007bff;
    outline: none;
    box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.25);
  }
`;

const Button = styled.button`
  padding: 12px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #e70d0dff;
  text-align: center;
  margin-top: 10px;
`;

const SuccessMessage = styled.p`
  color: #28a745;
  text-align: center;
  margin-top: 10px;
`;

const InfoMessage = styled.p`
  color: #007bff;
  text-align: center;
  margin-top: 10px;
  font-size: 0.9em;
  line-height: 1.4;
`;

const StallRenewalLogin: React.FC = () => {
  const [stallNumber, setStallNumber] = useState(''); // only numeric part
  const [telephoneNumber, setTelephoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const stallInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Ensure cursor always stays after prefix "GREMA-"
  useEffect(() => {
    if (stallInputRef.current && !isOtpSent) {
      const handleFocus = () => {
        const prefixLength = 6; // GREMA- is 6 chars
        stallInputRef.current!.setSelectionRange(prefixLength, prefixLength);
      };

      stallInputRef.current.addEventListener('focus', handleFocus);
      return () => {
        stallInputRef.current?.removeEventListener('focus', handleFocus);
      };
    }
  }, [isOtpSent]);

  const handleStallNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Remove GREMA- if present and keep only digits
    let numericPart = value.replace(/^GREMA-/, '').replace(/\D/g, '');
    setStallNumber(numericPart);
  };

  const getFullStallNumber = () => `GREMA-${stallNumber}`;

  const normalizePhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // If it's a Ugandan number, ensure it's in the +256 format
    if (cleaned.startsWith('0')) {
      cleaned = '256' + cleaned.substring(1);
    }
    
    return cleaned;
  };

const sendOTPViaYoUganda = async (phone: string, otpCode: string): Promise<boolean> => {
  try {
    const functionUrl = 'https://us-central1-grema-62003.cloudfunctions.net/send_otp_via_yo_uganda';

    // DEBUG: Log what we're sending
    console.log('Sending to API - Phone:', phone, 'OTP:', otpCode);
    console.log('OTP type:', typeof otpCode, 'length:', otpCode.length);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone: phone,
        otp: otpCode.toString(), // Ensure it's string
      }),
    });

    const data = await response.json();
    console.log('SMS API Response:', data);

    if (response.ok && data.status === 'OK') {
      return true;
    } else {
      console.error('Failed to send OTP:', data);
      setError(data.message || 'Failed to send OTP. Please try again.');
      return false;
    }
  } catch (error) {
    console.error('Error calling Firebase function:', error);
    setError('Network error. Please check your connection and try again.');
    return false;
  }
};

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (isOtpSent) {
      await verifyOTP();
    } else {
      await verifyStallAndSendOTP();
    }
  };

  const verifyStallAndSendOTP = async () => {
  try {
    const fullStallNumber = getFullStallNumber();
    const normalizedPhone = normalizePhoneNumber(telephoneNumber);
    
    console.log('Looking for stall:', fullStallNumber);
    console.log('With phone:', normalizedPhone);
    
    const stallsRef = collection(db, 'stalls');
    const stallQuery = query(
      stallsRef,
      where('stallNumber', '==', fullStallNumber)
    );

    const querySnapshot = await getDocs(stallQuery);

    if (querySnapshot.empty) {
      setError('Invalid stall number. Please check your details.');
      setLoading(false);
      return;
    }

    let stallFound = false;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const storedPhone = data.businessPhone ? normalizePhoneNumber(data.businessPhone) : '';
      
      if (storedPhone === normalizedPhone) {
        stallFound = true;
      }
    });

    if (!stallFound) {
      setError('The telephone number does not match the one registered for this stall.');
      setLoading(false);
      return;
    }

    // Generate OTP (ensure it's exactly 6 digits)
    const generatedOtp = Math.floor(100000 + Math.random() * 900000).toString();
    
    console.log('Generated OTP:', generatedOtp);
    
    // Send OTP via Firebase function
    const otpSent = await sendOTPViaYoUganda(normalizedPhone, generatedOtp);
    
    if (otpSent) {
      setIsOtpSent(true);
      setSuccess('OTP has been sent to your phone. Please enter it below.');
      
      // Store the OTP in sessionStorage for verification
      sessionStorage.setItem('stall_otp', generatedOtp);
      sessionStorage.setItem('stall_otp_phone', normalizedPhone);
      sessionStorage.setItem('stall_otp_time', Date.now().toString());
      
      console.log('OTP stored in session:', generatedOtp);
    }
    
  } catch (err: any) {
    console.error('Error during verification process:', err);
    setError(`Verification failed: ${err.message}. Please try again.`);
  } finally {
    setLoading(false);
  }
};


const verifyOTP = async () => {
  try {
    setError(null);
    setSuccess(null);

    const storedOtp = sessionStorage.getItem('stall_otp');
    const storedPhone = sessionStorage.getItem('stall_otp_phone');
    const storedTime = sessionStorage.getItem('stall_otp_time');
    const normalizedPhone = normalizePhoneNumber(telephoneNumber);

    // Debug logging
    console.log('=== OTP VERIFICATION DEBUG ===');
    console.log('Stored OTP:', storedOtp);
    console.log('Entered OTP:', otp);
    console.log('Stored Phone:', storedPhone);
    console.log('Current Phone:', normalizedPhone);

    if (!storedOtp || !storedPhone || !storedTime) {
      setError('No OTP session found. Please request a new OTP.');
      setIsOtpSent(false);
      return;
    }

    // Check if OTP expired (10 minutes)
    const storedTimeNum = parseInt(storedTime);
    if (Date.now() - storedTimeNum > 10 * 60 * 1000) {
      setError('OTP has expired. Please request a new one.');
      setIsOtpSent(false);
      return;
    }

    // Phone verification
    if (normalizedPhone !== storedPhone) {
      setError('Phone number does not match. Please try again.');
      return;
    }

    // OTP verification - remove any whitespace and compare
    const cleanEnteredOtp = otp.replace(/\s/g, '');
    const cleanStoredOtp = storedOtp.replace(/\s/g, '');

    console.log('Cleaned entered OTP:', cleanEnteredOtp);
    console.log('Cleaned stored OTP:', cleanStoredOtp);
    console.log('Match:', cleanEnteredOtp === cleanStoredOtp);

    if (cleanEnteredOtp === cleanStoredOtp) {
      setSuccess('OTP verified successfully!');
      
      const fullStallNumber = getFullStallNumber();
      setTimeout(() => {
        navigate(`/stall-renewal/${fullStallNumber}`);
        // Clear session storage
        sessionStorage.removeItem('stall_otp');
        sessionStorage.removeItem('stall_otp_phone');
        sessionStorage.removeItem('stall_otp_time');
      }, 1000);
    } else {
      setError('Invalid OTP. Please try again.');
    }
  } catch (error: any) {
    console.error('Error during OTP verification:', error);
    setError(`Verification failed: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

const handleResendOTP = async () => {
  setLoading(true);
  setError(null);
  await verifyStallAndSendOTP();
};

  return (
    <FormContainer>
      <FormBox onSubmit={handleLogin}>
        <Title>Stall Renewal Access</Title>

        {!isOtpSent && (
          <>
            <div style={{ position: 'relative' }}>
              <Input
                ref={stallInputRef}
                type="text"
                value={getFullStallNumber()}
                onChange={handleStallNumberChange}
                required
                disabled={loading}
                style={{ paddingLeft: '70px' }}
              />             
            </div>
            <Input
              type="tel"
              placeholder="Business Telephone Number (e.g., 0771234567)"
              value={telephoneNumber}
              onChange={(e) => setTelephoneNumber(e.target.value)}
              required
              disabled={loading}
            />
          </>
        )}

        {isOtpSent && (
          <Input
            type="text"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            required
            disabled={loading}
          />
        )}

        <Button type="submit" disabled={loading}>
          {loading ? 'Processing...' : isOtpSent ? 'Verify OTP' : 'Send OTP'}
        </Button>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
      </FormBox>
      <InfoMessage>
        This form allows you to access your stall renewal page. After verification, you'll be redirected to the renewal page.
      </InfoMessage>
    </FormContainer>
  );
};

export default StallRenewalLogin;